/**
 * ConversationLockController — pilote le cycle de vie des verrous de
 * conversation. Monté UNE seule fois au shell (cf CockpitShell), il rend `null`.
 *
 * Pourquoi un controller global et pas le FanPanel ? Parce qu'un TabGroup ne
 * monte QUE l'onglet actif (cf TabGroup.tsx). Si le verrou vivait dans le
 * FanPanel, changer d'onglet le démonterait et libérerait le verrou. Le verrou
 * doit refléter « la conv fait partie de mes onglets » = l'ensemble des onglets
 * fan OUVERTS dans le layout, pas l'onglet monté.
 *
 * Comportement (rôles verrouillables uniquement — no-op pour les superviseurs) :
 *   - À chaque changement de l'ensemble des onglets fan ouverts → reconcileLocks
 *     (acquiert les nouveaux, libère ceux fermés).
 *   - Heartbeat toutes les LOCK_HEARTBEAT_MS → reconcileLocks (prolonge le TTL).
 * La libération « dure » (logout, crash, fermeture brutale) est rattrapée par le
 * TTL + le cron Convex `cleanupExpired`.
 */
import { useEffect, useMemo, useRef } from "react";
import { useAtomValue } from "jotai";
import { useMutation, useQuery } from "convex/react";

import { api } from "@/../convex/_generated/api";
import { surEcritureAuFan } from "@/core/api/ecritureAuFan";
import { layoutAtom } from "@/core/state";

import {
  collectOpenFanConversationIds,
  LOCK_HEARTBEAT_MS,
  regieFocusConversationIdsAtom,
  unionLockedConversationIds,
} from "./lockLogic";
import { useConversationLocks } from "./useConversationLocks";

export function ConversationLockController() {
  const layout = useAtomValue(layoutAtom);
  const { accountId, isBypass, reconcile } = useConversationLocks();
  const gate = useQuery(
    api.shiftGate.getGateForCurrentUser,
    accountId && !isBypass ? { accountId } : "skip",
  );
  const isOnBreak = gate?.activeSession?.breakStartedAt != null;

  // Ensemble de base = onglets fan ouverts, UNIONNÉ avec les cartes focus de
  // La Régie (atom, `[]` par défaut). Quand La Régie n'est pas montée l'atom
  // reste vide → l'union est un no-op et le comportement est identique à avant.
  // Le changement de l'atom rentre dans `openIdsKey` → réconciliation immédiate.
  const regieFocusIds = useAtomValue(regieFocusConversationIdsAtom);
  const openIds = useMemo(
    () =>
      unionLockedConversationIds(
        collectOpenFanConversationIds(layout),
        regieFocusIds,
      ),
    [layout, regieFocusIds],
  );
  const effectiveOpenIds = useMemo(
    () => (isOnBreak ? [] : openIds),
    [isOnBreak, openIds],
  );
  // Clé stable = signal de changement de l'ENSEMBLE (un resize de split qui ne
  // change pas les onglets fan ne doit pas re-déclencher la réconciliation).
  const openIdsKey = useMemo(
    () => [...effectiveOpenIds].sort().join(","),
    [effectiveOpenIds],
  );

  // Ref pour que l'intervalle lise toujours l'ensemble courant sans se recréer
  // sur chaque render (seul openIdsKey doit recréer l'effet).
  const openIdsRef = useRef(effectiveOpenIds);
  openIdsRef.current = effectiveOpenIds;

  // Tout envoi à un fan, par n'importe quelle route, remet son chrono à
  // l'heure : ses discussions ne sont libérées qu'après 15 min sans écrire à
  // PERSONNE (cf convex/conversationLocks.ts, `WRITE_IDLE_MS`).
  const recordOutgoing = useMutation(
    api.conversationLocks.recordOutgoingMessage,
  );
  useEffect(() => {
    if (!accountId || isBypass) return;
    return surEcritureAuFan((conversationId) => {
      void recordOutgoing({ accountId, conversationId }).catch(() => {
        // Effet de bord : l'envoi est déjà parti. Le suivant rattrapera.
      });
    });
  }, [accountId, isBypass, recordOutgoing]);

  useEffect(() => {
    // Superviseurs : aucun verrou posé. Pas d'account résolu : rien à faire.
    if (!accountId || isBypass) return;

    let cancelled = false;
    const run = () => {
      if (!cancelled) void reconcile(openIdsRef.current);
    };

    // Réconcilie immédiatement au changement (acquiert nouveaux / libère fermés).
    run();

    // Heartbeat seulement s'il reste des onglets à maintenir en vie. Le passage
    // à 0 onglet a déjà déclenché un `run()` ci-dessus (qui libère le dernier).
    if (openIdsRef.current.length === 0) {
      return () => {
        cancelled = true;
      };
    }
    const interval = setInterval(run, LOCK_HEARTBEAT_MS);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // openIdsKey est le proxy de changement de l'ensemble (openIds lu via ref).
  }, [accountId, isBypass, openIdsKey, reconcile]);

  return null;
}
